/**
 * Collapsible, tabbed panel showing all rich diagnostic data captured with a
 * feedback ticket. Each tab is color-coded by data type. Collapsed by default.
 */
import { useMemo, useState } from 'react';
import { Box, Chip, Collapse, IconButton, Paper, Tab, Tabs, Typography } from '@mui/material';
import {
  ExpandMore, ExpandLess, TouchApp, CloudSync, BugReport,
  CloudOff, ErrorOutline, CenterFocusStrong, Storage, Speed, Info,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { DomStateView } from './DomStateView';
import { SummaryView } from './SummaryView';
import type { FeedbackTicketDetail } from '../../types/feedback';

interface Props { ticket: FeedbackTicketDetail; isAdmin?: boolean }

function safeParse<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

export const DiagnosticsPanel = ({ ticket, isAdmin }: Props) => {
  const theme = useTheme();
  const [open, setOpen] = useState(true);  // Expanded by default
  const [active, setActive] = useState(0);  // First tab (Summary) active by default

  const consoleErrors = safeParse<any[]>(ticket.ConsoleErrors, []);
  const networkErrors = safeParse<any[]>(ticket.NetworkErrors, []);
  const interactions = safeParse<any[]>(ticket.InteractionTrail, []);
  const apiCalls = safeParse<any[]>(ticket.RecentApiCalls, []);
  const rejections = safeParse<any[]>(ticket.UnhandledRejections, []);
  const domState = safeParse<any>(ticket.DomState, null);
  const storageKeys = safeParse<{ localStorage: string[]; sessionStorage: string[] }>(
    ticket.StorageKeys, { localStorage: [], sessionStorage: [] },
  );

  const tabs = useMemo(() => {
    const t: { key: string; label: string; count?: number; color: string; icon: JSX.Element; render: () => JSX.Element }[] = [];

    // Summary tab — always first, always present
    t.push({
      key: 'summary', label: 'Summary',
      color: theme.palette.primary.main, icon: <Info sx={{ fontSize: 16 }} />,
      render: () => <SummaryView ticket={ticket} isAdmin={isAdmin} />,
    });

    if (interactions.length > 0) t.push({
      key: 'actions', label: 'Actions', count: interactions.length,
      color: theme.palette.primary.main, icon: <TouchApp sx={{ fontSize: 16 }} />,
      render: () => (
        <Box>
          {interactions.map((a: any, i: number) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.3, fontFamily: 'monospace', alignItems: 'baseline' }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', flexShrink: 0, width: 72 }}>
                {new Date(a.timestamp).toLocaleTimeString().slice(0, 8)}
              </Typography>
              <Chip label={a.type} size="small" sx={{ fontSize: '0.6rem', height: 16, flexShrink: 0, minWidth: 50 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {a.target}{a.detail ? ` [${a.detail}]` : ''}
              </Typography>
            </Box>
          ))}
        </Box>
      ),
    });

    if (apiCalls.length > 0) t.push({
      key: 'api', label: 'API Calls', count: apiCalls.length,
      color: theme.palette.info.main, icon: <CloudSync sx={{ fontSize: 16 }} />,
      render: () => (
        <Box>
          {apiCalls.map((a: any, i: number) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.3, alignItems: 'baseline' }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', flexShrink: 0, width: 72, fontFamily: 'monospace' }}>
                {new Date(a.timestamp).toLocaleTimeString().slice(0, 8)}
              </Typography>
              <Chip label={a.method} size="small" sx={{ fontSize: '0.6rem', height: 16, flexShrink: 0, width: 56 }} />
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}>{a.url}</Typography>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: a.status >= 400 ? 'error.main' : 'success.main', flexShrink: 0 }}>
                {a.status}
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', flexShrink: 0, width: 50, textAlign: 'right' }}>{a.durationMs}ms</Typography>
            </Box>
          ))}
        </Box>
      ),
    });

    if (consoleErrors.length > 0) t.push({
      key: 'console', label: 'Console', count: consoleErrors.length,
      color: theme.palette.error.main, icon: <BugReport sx={{ fontSize: 16 }} />,
      render: () => (
        <Box>
          {consoleErrors.map((e: any, i: number) => (
            <Box key={i} sx={{ mb: 1, pb: 1, borderBottom: i < consoleErrors.length - 1 ? `1px solid ${theme.palette.divider}` : 'none' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.3 }}>
                <Chip label={e.level} size="small" sx={{ fontSize: '0.6rem', height: 16,
                  bgcolor: e.level === 'error' ? '#ffebee' : '#fff3e0',
                  color: e.level === 'error' ? '#d32f2f' : '#e65100' }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem' }}>{new Date(e.timestamp).toLocaleTimeString()}</Typography>
              </Box>
              <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all', lineHeight: 1.4 }}>{e.message}</Typography>
            </Box>
          ))}
        </Box>
      ),
    });

    if (networkErrors.length > 0) t.push({
      key: 'net', label: 'Network', count: networkErrors.length,
      color: theme.palette.warning.main, icon: <CloudOff sx={{ fontSize: 16 }} />,
      render: () => (
        <Box>
          {networkErrors.map((e: any, i: number) => (
            <Typography key={i} variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', mb: 0.3 }}>
              {e.method} {e.url} → <strong style={{ color: theme.palette.error.main }}>{e.status}</strong>
            </Typography>
          ))}
        </Box>
      ),
    });

    if (rejections.length > 0) t.push({
      key: 'rej', label: 'Rejections', count: rejections.length,
      color: '#d32f2f', icon: <ErrorOutline sx={{ fontSize: 16 }} />,
      render: () => (
        <Box>
          {rejections.map((r: any, i: number) => (
            <Box key={i} sx={{ mb: 0.75 }}>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.62rem', display: 'block' }}>{new Date(r.timestamp).toLocaleTimeString()}</Typography>
              <Typography variant="caption" sx={{ display: 'block', fontFamily: 'monospace', fontSize: '0.7rem', color: 'error.main', wordBreak: 'break-all' }}>{r.reason}</Typography>
            </Box>
          ))}
        </Box>
      ),
    });

    if (domState) t.push({
      key: 'dom', label: 'DOM State',
      count: (domState.buttons?.length || 0) + (domState.inputs?.length || 0) || undefined,
      color: theme.palette.secondary.main, icon: <CenterFocusStrong sx={{ fontSize: 16 }} />,
      render: () => <DomStateView domState={domState} />,
    });

    const storageCount = storageKeys.localStorage.length + storageKeys.sessionStorage.length;
    if (storageCount > 0) t.push({
      key: 'storage', label: 'Storage', count: storageCount,
      color: '#0097a7', icon: <Storage sx={{ fontSize: 16 }} />,
      render: () => (
        <Box sx={{ fontFamily: 'monospace' }}>
          {storageKeys.localStorage.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3 }}>localStorage ({storageKeys.localStorage.length})</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>{storageKeys.localStorage.join(', ')}</Typography>
            </Box>
          )}
          {storageKeys.sessionStorage.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.3 }}>sessionStorage ({storageKeys.sessionStorage.length})</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', wordBreak: 'break-all' }}>{storageKeys.sessionStorage.join(', ')}</Typography>
            </Box>
          )}
        </Box>
      ),
    });

    if (ticket.PerformanceInfo) t.push({
      key: 'perf', label: 'Performance',
      color: theme.palette.success.main, icon: <Speed sx={{ fontSize: 16 }} />,
      render: () => <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{ticket.PerformanceInfo}</Typography>,
    });

    return t;
  }, [consoleErrors, networkErrors, interactions, apiCalls, rejections, domState, storageKeys, ticket, isAdmin, theme]);

  if (tabs.length === 0) return null;

  const totalItems = tabs.reduce((acc, t) => acc + (t.count || 0), 0);
  const currentTab = tabs[Math.min(active, tabs.length - 1)];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 1.5, mb: 2.5, overflow: 'hidden' }}>
      {/* Header — click to collapse/expand */}
      <Box onClick={() => setOpen((v) => !v)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25,
          cursor: 'pointer', userSelect: 'none',
          bgcolor: open ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
          borderBottom: open ? `1px solid ${theme.palette.divider}` : 'none',
          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
        }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem', flex: 1 }}>
          Diagnostic Data
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {tabs.map((t) => (
            <Chip key={t.key} size="small" icon={t.icon}
              label={t.count !== undefined ? t.count : '—'}
              sx={{
                fontSize: '0.68rem', height: 22, fontWeight: 600,
                bgcolor: alpha(t.color, 0.1), color: t.color,
                border: `1px solid ${alpha(t.color, 0.25)}`,
                '& .MuiChip-icon': { color: t.color, ml: 0.5, mr: -0.3 },
              }} />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: '0.7rem' }}>
          {totalItems > 0 ? `${totalItems} items` : ''}
        </Typography>
        <IconButton size="small" sx={{ p: 0.5 }}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      <Collapse in={open}>
        <Tabs
          value={Math.min(active, tabs.length - 1)}
          onChange={(_, v) => setActive(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{ sx: { backgroundColor: currentTab?.color } }}
          sx={{
            minHeight: 40, px: 1, borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': { minHeight: 40, py: 0.5, px: 1.5, textTransform: 'none', fontSize: '0.78rem', color: 'text.secondary' },
            '& .Mui-selected': { color: `${currentTab?.color} !important` },
          }}
        >
          {tabs.map((t) => (
            <Tab key={t.key} icon={t.icon} iconPosition="start" label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                {t.label}
                {t.count !== undefined && (
                  <Chip label={t.count} size="small" sx={{
                    height: 16, fontSize: '0.62rem', fontWeight: 600,
                    bgcolor: alpha(t.color, 0.15), color: t.color,
                  }} />
                )}
              </Box>
            } />
          ))}
        </Tabs>
        <Box sx={{
          p: 2, maxHeight: 360, overflow: 'auto',
          borderLeft: `3px solid ${currentTab?.color}`,
          bgcolor: alpha(currentTab?.color || '#000', 0.02),
        }}>
          {currentTab?.render()}
        </Box>
      </Collapse>
    </Paper>
  );
};

